import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';
import { isTokenExpired } from './utils/jwt';

const WorkspaceContext = createContext();

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

const initialCounts = { rawTotal: 0, cutsTotal: 0, generatedTotal: 0 };

export const WorkspaceProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workspaceCounts, setWorkspaceCountsState] = useState(initialCounts);

  const setWorkspaceCounts = useCallback((updates) => {
    setWorkspaceCountsState(prev => ({ ...prev, ...updates }));
  }, []);

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    return token && !isTokenExpired(token);
  };

  // Load user's workspaces
  const loadWorkspaces = async () => {
    // Don't load workspaces if user is not authenticated
    if (!isAuthenticated()) {
      console.log('User not authenticated, skipping workspace load');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.getWorkspaces();
      // Merge with prior list so a slow GET (started before invite join) does not drop
      // the new workspace from the sidebar dropdown — joinWorkspace prepends it locally first.
      setWorkspaces((prev) => {
        const serverList = response.data;
        const serverIds = new Set(serverList.map((ws) => ws._id));
        const notYetOnServer = prev.filter((ws) => !serverIds.has(ws._id));
        return notYetOnServer.length ? [...notYetOnServer, ...serverList] : serverList;
      });

      // Use functional update so a stale in-flight load (from provider mount) cannot
      // overwrite currentWorkspace after invite join — the join effect closed over
      // currentWorkspace === null, but React state may already be the invited workspace.
      setCurrentWorkspace((prev) => {
        if (prev) {
          const updated = response.data.find((ws) => ws._id === prev._id);
          return updated || prev;
        }
        if (response.data.length === 0) return null;

        const lastWorkspaceId = localStorage.getItem('lastWorkspaceId');
        if (lastWorkspaceId) {
          const savedWorkspace = response.data.find((ws) => ws._id === lastWorkspaceId);
          if (savedWorkspace) {
            console.log('WorkspaceContext: Restored last used workspace:', lastWorkspaceId);
            return savedWorkspace;
          }
          localStorage.removeItem('lastWorkspaceId');
          console.log('WorkspaceContext: Last used workspace no longer exists, removed from localStorage');
        }

        return response.data[0];
      });
    } catch (err) {
      console.error('Error loading workspaces:', err);
      setError(err.response?.data?.error || 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  };

  // Create new workspace
  const createWorkspace = async (workspaceData) => {
    if (!isAuthenticated()) {
      throw new Error('User not authenticated');
    }

    try {
      setLoading(true);
      setError(null);
      console.log('[WORKSPACE CONTEXT] Creating workspace with data:', workspaceData);
      const response = await api.createWorkspace(workspaceData);
      const newWorkspace = response.data;
      console.log('[WORKSPACE CONTEXT] Created workspace:', newWorkspace);
      
      setWorkspaces(prev => [newWorkspace, ...prev]);
      setCurrentWorkspace(newWorkspace);
      
      // Save the newly created workspace as the last used workspace
      localStorage.setItem('lastWorkspaceId', newWorkspace._id);
      console.log('WorkspaceContext: Saved new workspace ID to localStorage:', newWorkspace._id);
      
      return newWorkspace;
    } catch (err) {
      console.error('Error creating workspace:', err);
      setError(err.response?.data?.error || 'Failed to create workspace');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update workspace
  const updateWorkspace = async (workspaceId, updateData, options = {}) => {
    if (!isAuthenticated()) {
      throw new Error('User not authenticated');
    }

    try {
      // Only set global loading for non-profile updates
      if (!options.skipLoading) {
        setLoading(true);
      }
      setError(null);
      const response = await api.updateWorkspace(workspaceId, updateData);
      const updatedWorkspace = response.data;
      
      setWorkspaces(prev => 
        prev.map(ws => ws._id === workspaceId ? updatedWorkspace : ws)
      );
      
      // Update currentWorkspace if it's the same workspace
      if (currentWorkspace?._id === workspaceId) {
        setCurrentWorkspace(updatedWorkspace);
      }
      
      return updatedWorkspace;
    } catch (err) {
      console.error('Error updating workspace:', err);
      setError(err.response?.data?.error || 'Failed to update workspace');
      throw err;
    } finally {
      if (!options.skipLoading) {
        setLoading(false);
      }
    }
  };

  // Delete workspace
  const deleteWorkspace = async (workspaceId) => {
    try {
      setLoading(true);
      setError(null);
      await api.deleteWorkspace(workspaceId);
      
      setWorkspaces(prev => prev.filter(ws => ws._id !== workspaceId));
      
      // If we deleted the current workspace, switch to another one
      if (currentWorkspace?._id === workspaceId) {
        const remainingWorkspaces = workspaces.filter(ws => ws._id !== workspaceId);
        const newCurrentWorkspace = remainingWorkspaces.length > 0 ? remainingWorkspaces[0] : null;
        setCurrentWorkspace(newCurrentWorkspace);
        
        // Update localStorage with the new workspace or remove it if no workspaces remain
        if (newCurrentWorkspace) {
          localStorage.setItem('lastWorkspaceId', newCurrentWorkspace._id);
        } else {
          localStorage.removeItem('lastWorkspaceId');
        }
      } else {
        // If the deleted workspace was the last used one, remove it from localStorage
        const lastWorkspaceId = localStorage.getItem('lastWorkspaceId');
        if (lastWorkspaceId === workspaceId) {
          localStorage.removeItem('lastWorkspaceId');
        }
      }
    } catch (err) {
      console.error('Error deleting workspace:', err);
      setError(err.response?.data?.error || 'Failed to delete workspace');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Switch to a different workspace
  const switchWorkspace = async (workspaceId) => {
    console.log('WorkspaceContext: Switching to workspace:', workspaceId);
    
    if (!isAuthenticated()) {
      throw new Error('User not authenticated');
    }

    try {
      setLoading(true);
      setError(null);
      console.log('WorkspaceContext: Fetching workspace data...');
      const response = await api.getWorkspace(workspaceId);
      console.log('WorkspaceContext: Workspace data received:', response.data);
      setCurrentWorkspace(response.data);
      
      // Save the selected workspace ID to localStorage
      localStorage.setItem('lastWorkspaceId', workspaceId);
      console.log('WorkspaceContext: Saved last workspace ID to localStorage:', workspaceId);
    } catch (err) {
      console.error('Error switching workspace:', err);
      setError(err.response?.data?.error || 'Failed to switch workspace');
    } finally {
      setLoading(false);
    }
  };

  // Select workspace (alias for switchWorkspace)
  const selectWorkspace = switchWorkspace;

  // Generate invite link
  const generateInviteLink = async (workspaceId) => {
    try {
      const response = await api.generateInviteLink(workspaceId);
      return response.data;
    } catch (err) {
      console.error('Error generating invite link:', err);
      setError(err.response?.data?.error || 'Failed to generate invite link');
      throw err;
    }
  };

  // Join workspace via invite token
  const joinWorkspace = useCallback(async (inviteToken, role = 'editor') => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.joinWorkspace(inviteToken, role);
      const newWorkspace = response.data;
      
      setWorkspaces(prev => [newWorkspace, ...prev]);
      setCurrentWorkspace(newWorkspace);
      
      // Save the joined workspace as the last used workspace
      localStorage.setItem('lastWorkspaceId', newWorkspace._id);
      console.log('WorkspaceContext: Saved joined workspace ID to localStorage:', newWorkspace._id);
      
      return newWorkspace;
    } catch (err) {
      console.error('Error joining workspace:', err);
      setError(err.response?.data?.error || 'Failed to join workspace');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update member role
  const updateMemberRole = async (workspaceId, memberId, role) => {
    try {
      await api.updateMemberRole(workspaceId, memberId, role);
      
      // Refresh current workspace to get updated member data
      if (currentWorkspace?._id === workspaceId) {
        await switchWorkspace(workspaceId);
      }
    } catch (err) {
      console.error('Error updating member role:', err);
      setError(err.response?.data?.error || 'Failed to update member role');
      throw err;
    }
  };

  // Remove member
  const removeMember = async (workspaceId, memberId) => {
    try {
      await api.removeMember(workspaceId, memberId);
      
      // Refresh current workspace to get updated member data
      if (currentWorkspace?._id === workspaceId) {
        await switchWorkspace(workspaceId);
      }
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.response?.data?.error || 'Failed to remove member');
      throw err;
    }
  };

  // Leave workspace
  const leaveWorkspace = async (workspaceId) => {
    try {
      setLoading(true);
      setError(null);
      await api.leaveWorkspace(workspaceId);
      
      setWorkspaces(prev => prev.filter(ws => ws._id !== workspaceId));
      
      // If we left the current workspace, switch to another one
      if (currentWorkspace?._id === workspaceId) {
        const remainingWorkspaces = workspaces.filter(ws => ws._id !== workspaceId);
        const newCurrentWorkspace = remainingWorkspaces.length > 0 ? remainingWorkspaces[0] : null;
        setCurrentWorkspace(newCurrentWorkspace);
        
        // Update localStorage with the new workspace or remove it if no workspaces remain
        if (newCurrentWorkspace) {
          localStorage.setItem('lastWorkspaceId', newCurrentWorkspace._id);
        } else {
          localStorage.removeItem('lastWorkspaceId');
        }
      } else {
        // If the left workspace was the last used one, remove it from localStorage
        const lastWorkspaceId = localStorage.getItem('lastWorkspaceId');
        if (lastWorkspaceId === workspaceId) {
          localStorage.removeItem('lastWorkspaceId');
        }
      }
    } catch (err) {
      console.error('Error leaving workspace:', err);
      setError(err.response?.data?.error || 'Failed to leave workspace');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Check if user has permission in current workspace
  const hasPermission = (requiredRole = 'viewer') => {
    if (!currentWorkspace) return false;
    
    const roleHierarchy = { 'viewer': 1, 'editor': 2, 'admin': 3 };
    
    // First try to get userRole from the workspace object
    let userRole = currentWorkspace.userRole;
    
    // If userRole is not set, try to find it from the members array
    if (!userRole && currentWorkspace.members) {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const userId = payload.userId || payload.id; // Try both userId and id fields
          
          const userMember = currentWorkspace.members.find(member => {
            const memberUserId = member.userId._id || member.userId;
            return memberUserId === userId;
          });
          
          if (userMember) {
            userRole = userMember.role;
          }
        } catch (error) {
          console.error('[PERMISSION] Error parsing token:', error);
        }
      }
    }
    
    return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
  };

  // Get user role in current workspace
  const getUserRole = () => {
    return currentWorkspace?.userRole || null;
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Load workspaces on mount (only if authenticated)
  useEffect(() => {
    if (isAuthenticated()) {
      loadWorkspaces();
    }
  }, []);

  // Reset counts when switching workspace so sidebar doesn't show stale numbers
  useEffect(() => {
    if (!currentWorkspace) setWorkspaceCountsState(initialCounts);
  }, [currentWorkspace?._id]);

  const value = {
    workspaces,
    currentWorkspace,
    loading,
    error,
    workspaceCounts,
    setWorkspaceCounts,
    loadWorkspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    switchWorkspace,
    selectWorkspace,
    generateInviteLink,
    joinWorkspace,
    updateMemberRole,
    removeMember,
    leaveWorkspace,
    hasPermission,
    getUserRole,
    clearError
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContext;